import React from 'react';
import { jsPDF } from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, FileDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Report pro vedení: co se nevyplatí opravovat, návrh investic, přehled BOZP.
 */
export const ManagementReport = ({
  replaceRecommendations = [],
  bozpSummary = { total: 0, bySeverity: { low: 0, medium: 0, high: 0 } },
  onClose,
}) => {
  const { t, language } = useLanguage();
  const dateLocale = language === 'cs' ? 'cs-CZ' : 'en-GB';

  const handleDownloadPdf = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    let y = 20;
    const lineH = 7;
    const margin = 20;

    doc.setFontSize(18);
    doc.text(t('reportForManagement'), margin, y);
    y += lineH;
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Facility Management System · ${new Date().toLocaleDateString(dateLocale)}`, margin, y);
    doc.setTextColor(0, 0, 0);
    y += lineH + 4;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t('replaceRecommendationsTitle'), margin, y);
    y += lineH;
    doc.setFont('helvetica', 'normal');

    if (replaceRecommendations.length === 0) {
      doc.setFontSize(10);
      doc.text(t('noReplacements'), margin, y);
      y += lineH + 6;
    } else {
      doc.setFontSize(9);
      replaceRecommendations.forEach((item) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${item.assetName} (${item.buildingName})`, margin, y);
        y += lineH * 0.8;
        doc.text(`  ${(item.repairCostTotal / 1000).toFixed(0)}k Kč · ${(item.replacementCost / 1000).toFixed(0)}k Kč`, margin, y);
        y += lineH + 2;
      });
      y += 4;
    }

    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t('investmentProposalTitle'), margin, y);
    y += lineH;
    doc.setFont('helvetica', 'normal');
    if (replaceRecommendations.length === 0) {
      doc.setFontSize(10);
      doc.text(t('noInvestments'), margin, y);
      y += lineH + 6;
    } else {
      doc.setFontSize(9);
      replaceRecommendations.forEach((item) => {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(`${item.assetName} — ${(item.replacementCost / 1000).toFixed(0)}k Kč`, margin, y);
        y += lineH + 1;
      });
      y += 4;
    }

    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(t('bozpOverviewTitle'), margin, y);
    y += lineH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`${t('bozpTotal')}: ${bozpSummary.total}`, margin, y);
    y += lineH;
    doc.text(`${t('bozpLow')}: ${bozpSummary.bySeverity?.low ?? 0}`, margin, y);
    y += lineH * 0.9;
    doc.text(`${t('bozpMedium')}: ${bozpSummary.bySeverity?.medium ?? 0}`, margin, y);
    y += lineH * 0.9;
    doc.text(`${t('bozpHigh')}: ${bozpSummary.bySeverity?.high ?? 0}`, margin, y);

    doc.save(`report-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          {t('reportForManagement')}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadPdf} className="gap-2">
            <FileDown className="w-4 h-4" />
            {t('downloadPdf')}
          </Button>
          {onClose && (
            <Button variant="secondary" onClick={onClose}>
              {t('close')}
            </Button>
          )}
        </div>
      </div>

      <div className="report-for-print space-y-6 bg-background p-4 print:p-0">
        <div className="border-b border-border pb-4">
          <h1 className="text-2xl font-bold text-foreground">{t('reportForManagement')}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Facility Management System · {new Date().toLocaleDateString(dateLocale)}
          </p>
        </div>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>{t('replaceRecommendationsTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {replaceRecommendations.length === 0 ? (
              <p className="text-muted-foreground">{t('noReplacements')}</p>
            ) : (
              <ul className="space-y-2">
                {replaceRecommendations.map((item, i) => (
                  <li key={i} className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
                    <span className="font-medium text-foreground">{item.assetName}</span>
                    <span className="text-muted-foreground">{item.buildingName}</span>
                    <span className="text-destructive font-medium">
                      {(item.repairCostTotal / 1000).toFixed(0)}k Kč
                    </span>
                    <span className="text-success font-medium">
                      {(item.replacementCost / 1000).toFixed(0)}k Kč
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>{t('investmentProposalTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {replaceRecommendations.length === 0 ? (
              <p className="text-muted-foreground">{t('noInvestments')}</p>
            ) : (
              <ul className="space-y-2">
                {replaceRecommendations.map((item, i) => (
                  <li key={i} className="flex justify-between gap-4 py-2 border-b border-border last:border-0">
                    <span className="font-medium text-foreground">{item.assetName}</span>
                    <span className="text-foreground">
                      {(item.replacementCost / 1000).toFixed(0)}k Kč
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-soft">
          <CardHeader>
            <CardTitle>{t('bozpOverviewTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground font-medium mb-2">{t('bozpTotal')}: {bozpSummary.total}</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>{t('bozpLow')}: {bozpSummary.bySeverity?.low ?? 0}</li>
              <li>{t('bozpMedium')}: {bozpSummary.bySeverity?.medium ?? 0}</li>
              <li>{t('bozpHigh')}: {bozpSummary.bySeverity?.high ?? 0}</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
